import { Injectable, signal, inject } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { BiometricAuthRequest, BiometricAuthResponse } from './biometric-auth.types';
import { CertificadosService } from './certificados.service';

export interface User {
  rut: string;
  nombre: string;
  apellidoPaterno: string;
  apellidoMaterno: string;
}

export interface LoginResponse {
  success: boolean;
  data: {
    user: User;
    expiresIn: number;
  };
  message: string;
}

export interface RefreshResponse {
  success: boolean;
  data: {
    expiresIn: number;
  };
  message: string;
}

export interface DatosPersonales {
  rut: string;
  nombres: string;
  apellidoPaterno: string;
  apellidoMaterno: string;
  fechaNacimiento: string;
  email: string;
  telFijo: string;
  telMovil: string;
  sexo: string;
  edad?: number;
  direccion: string;
  comuna: string;
  ciudad: string;
  region: string;
  codigoComuna?: number;
  codigoRegion?: number;
  codigoPaisResidencia?: string;
  paisResidencia: string;
  glosaPaisNacionalidad?: string;
  codigoPaisNacionalidad?: string;
  codigoAreaTelefono?: string;
  claseCotizante?: string;
  fechaAfiliacionAFPCapital?: string;
  fechaAfiliciacionSistema?: string;
  indicadorClienteFallecido?: string;
}

interface DatosPersonalesResponse {
  success: boolean;
  data: DatosPersonales;
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly API_URL = environment.apiUrl;
  private tokenRefreshTimer: any = null;
  private readonly tokenExpiresAt = signal<number | null>(null);
  private readonly certificadosService = inject(CertificadosService);
  private datosPersonalesPromise: Promise<DatosPersonales> | null = null;
  
  // Estado del usuario actual
  public currentUser = signal<User | null>(null);
  public isAuthenticated = signal<boolean>(false);
  public datosPersonales = signal<DatosPersonales | null>(null);
  public isLoadingDatos = signal<boolean>(false);

  constructor(private readonly router: Router) {
    // El estado de autenticación se maneja a través del login
    // Intentamos rehidratar la sesión al crear la instancia
    // pero no bloqueamos el constructor: usar `initializeSession()`
    // desde guardas o en el bootstrap de la app.
  }

  /**
   * Intenta reconstruir la sesión al cargar la aplicación.
   * - Llama a `refreshToken()` para validar cookies/refresh token del servidor
   * - Si tiene éxito, marca `isAuthenticated` y precarga datos.
   * Devuelve `true` si la sesión quedó válida, `false` si no.
   */
  async initializeSession(): Promise<boolean> {
    // Si ya estamos autenticados localmente, no hacemos nada
    if (this.isAuthenticated()) return true;

    try {
      // Intentar renovar token usando cookies/refresh almacenado en servidor
      const refreshResult = await this.refreshToken();
      if (refreshResult && refreshResult.success) {
        // Indicamos que ya estamos autenticados (refreshToken() ya programa renovación)
        this.isAuthenticated.set(true);
        // Intentar precarga de datos (no bloqueará si falla)
        this.preloadUserData().catch((e) => console.warn('Preload falló', e));
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  }

  /**
   * Login con RUT y contraseña
   */
  async login(rut: string, password: string): Promise<LoginResponse> {
    try {
      const response = await fetch(`${this.API_URL}/auth/login`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ rut, password }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Login fallido');
      }

      const data: LoginResponse = await response.json();
      
      // Actualizar estado
      this.currentUser.set(data.data.user);
      this.isAuthenticated.set(true);
      
      // Programar renovación automática del token
      this.scheduleTokenRefresh(data.data.expiresIn);
      
      // Precargar datos del usuario de forma centralizada
      await this.preloadUserData();
      
      return data;
    } catch (error) {
      this.isAuthenticated.set(false);
      throw error;
    }
  }

  /**
   * Autenticación biométrica usando dispositivo lector
   */
  async biometricLogin(payload: BiometricAuthRequest): Promise<BiometricAuthResponse> {
    try {
      const response = await fetch(`${this.API_URL}/auth/biometric`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Autenticación biométrica fallida');
      }

      const data: BiometricAuthResponse = await response.json();
      
      // Actualizar estado
      this.currentUser.set(data.data.user);
      this.isAuthenticated.set(true);
      
      // Programar renovación automática del token
      this.scheduleTokenRefresh(data.data.expiresIn);
      
      // Precargar datos del usuario de forma centralizada
      await this.preloadUserData();
      
      return data;
    } catch (error) {
      this.isAuthenticated.set(false);
      throw error;
    }
  }

  /**
   * Renueva el access token usando el refresh token
   */
  async refreshToken(): Promise<RefreshResponse> {
    try {
      const response = await fetch(`${this.API_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        // Refresh token inválido o expirado
        this.handleSessionExpired();
        throw new Error('Sesión expirada');
      }

      const data: RefreshResponse = await response.json();
      
      // Reprogramar siguiente renovación
      this.scheduleTokenRefresh(data.data.expiresIn);
      
      return data;
    } catch (error) {
      this.handleSessionExpired();
      throw error;
    }
  }

  /**
   * Cierra la sesión del usuario
   */
  async logout(): Promise<void> {
    try {
      await fetch(`${this.API_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });
    } catch (error) {
      console.error('Error en logout:', error);
    } finally {
      this.clearSession();
      this.router.navigate(['/']);
    }
  }

  /**
   * Programa la renovación automática del token
   * Se ejecuta 60 segundos antes de que expire para dejar margen de seguridad
   */
  private scheduleTokenRefresh(expiresIn: number): void {
    // Limpiar timer anterior si existe
    if (this.tokenRefreshTimer) {
      clearTimeout(this.tokenRefreshTimer);
    }

    // Calcular tiempo de expiración
    const expiresAtTime = Date.now() + (expiresIn * 1000);
    this.tokenExpiresAt.set(expiresAtTime);

    // Renovar 60 segundos antes de que expire para dejar margen de seguridad
    // Si el token expira en menos de 60 segundos, renovar al 80% del tiempo disponible
    const bufferTime = 60; // segundos
    const refreshTime = expiresIn > bufferTime 
      ? (expiresIn - bufferTime) * 1000 
      : Math.max(0, expiresIn * 0.8) * 1000;

    console.log(`Token expira en ${expiresIn} segundos. Renovación programada en ${refreshTime / 1000} segundos.`);

    this.tokenRefreshTimer = setTimeout(async () => {
      try {
        console.log('Renovando token automáticamente...');
        await this.refreshToken();
        console.log('Token renovado exitosamente');
      } catch (error) {
        console.error('Error al renovar token automáticamente:', error);
        this.handleSessionExpired();
      }
    }, refreshTime);
  }

  /**
   * Maneja el caso cuando la sesión expira
   */
  private handleSessionExpired(): void {
    console.log('Sesión expirada. Redirigiendo a login...');
    this.clearSession();
    this.router.navigate(['/']);
  }

  /**
   * Limpia el estado de la sesión
   */
  private clearSession(): void {
    if (this.tokenRefreshTimer) {
      clearTimeout(this.tokenRefreshTimer);
      this.tokenRefreshTimer = null;
    }
    this.currentUser.set(null);
    this.isAuthenticated.set(false);
    this.tokenExpiresAt.set(null);
    this.datosPersonales.set(null);
  }

  /**
   * Realiza una petición HTTP con manejo automático de autenticación
   */
  async fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
    let response = await fetch(url, {
      ...options,
      credentials: 'include',
      headers: {
        ...options.headers,
        'Content-Type': 'application/json',
      },
    });

    // Si recibimos 401, intentar renovar el token
    if (response.status === 401) {
      try {
        await this.refreshToken();
        // Reintentar la petición original
        response = await fetch(url, {
          ...options,
          credentials: 'include',
          headers: {
            ...options.headers,
            'Content-Type': 'application/json',
          },
        });
      } catch (error) {
        // Refresh falló, redirigir a login
        this.handleSessionExpired();
        throw error;
      }
    }

    return response;
  }

  /**
   * Obtiene el tiempo restante hasta la expiración del token (en segundos)
   */
  getTimeUntilExpiration(): number {
    const expiresAt = this.tokenExpiresAt();
    if (!expiresAt) return 0;
    return Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));
  }

  /**
   * Precarga secuencialmente todos los datos necesarios del usuario
   * 1. Certificados disponibles
   * 2. Datos personales/antecedentes del usuario
   */
  private async preloadUserData(): Promise<void> {
    try {
      console.log('AuthService: Iniciando precarga de datos del usuario...');
      
      // 1. Precargar certificados primero
      console.log('AuthService: Precargando certificados...');
      await this.certificadosService.getCertificados();
      console.log('AuthService: Certificados precargados exitosamente');
      
      // 2. Precargar datos personales después
      console.log('AuthService: Precargando datos personales...');
      await this.loadDatosPersonales();
      console.log('AuthService: Datos personales precargados exitosamente');
      
      console.log('AuthService: Precarga de datos completada');
    } catch (error) {
      console.error('AuthService: Error durante la precarga de datos:', error);
      // No lanzamos el error para que no bloquee el login
      // Los datos se pueden cargar posteriormente cuando se necesiten
    }
  }

  /**
   * Carga los datos personales del usuario autenticado
   * Si ya hay datos cargados, los retorna sin hacer una nueva petición
   * Si ya hay una carga en progreso, retorna esa promesa
   */
  async loadDatosPersonales(): Promise<DatosPersonales> {
    // Si ya hay datos cargados, retornarlos directamente
    const datosActuales = this.datosPersonales();
    if (datosActuales !== null) {
      console.log('AuthService: Datos personales ya están en caché, retornando...');
      return datosActuales;
    }

    // Si ya hay una carga en progreso, retornar esa promesa
    if (this.datosPersonalesPromise !== null) {
      console.log('AuthService: Ya hay una carga en progreso, retornando promesa existente...');
      return this.datosPersonalesPromise;
    }

    // Iniciar nueva carga
    console.log('AuthService: Iniciando nueva carga de datos personales...');
    this.isLoadingDatos.set(true);

    this.datosPersonalesPromise = this._loadDatosPersonalesFromApi();
    
    try {
      const datos = await this.datosPersonalesPromise;
      return datos;
    } finally {
      this.datosPersonalesPromise = null;
      this.isLoadingDatos.set(false);
    }
  }

  /**
   * Método privado que realiza la carga real desde la API
   */
  private async _loadDatosPersonalesFromApi(): Promise<DatosPersonales> {
    try {
      
      const url = `${this.API_URL}/client/personal-data`;
      
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      console.log('AuthService: Response status:', response.status);

      if (!response.ok) {
        if (response.status === 401) {
          console.log('AuthService: Token expirado, intentando refresh...');
          // Token expirado, intentar refresh
          await this.refreshToken();
          // Reintentar
          return this.loadDatosPersonales();
        }
        const errorText = await response.text();
        console.error('AuthService: Error en respuesta:', errorText);
        throw new Error('Error al cargar datos personales');
      }

      const responseData: DatosPersonalesResponse = await response.json();
      console.log('AuthService: Response completo:', responseData);
      
      // Extraer los datos del wrapper
      const datos: DatosPersonales = responseData.data;
      console.log('AuthService: Datos extraídos:', datos);
      
      this.datosPersonales.set(datos);
      console.log('AuthService: Signal actualizado con datos');
      
      return datos;
    } catch (error) {
      console.error('AuthService: Error al cargar datos personales:', error);
      throw error;
    }
  }

  /**
   * Actualiza los datos personales del usuario autenticado
   * @param datosNuevos - Objeto con los datos a actualizar
   * @returns Promise con la respuesta del servidor
   */
  async updateDatosPersonales(datosNuevos: {
    nombre: string;
    apellidoPaterno: string;
    apellidoMaterno: string;
    telefono: string;
    celular: string;
    email: string;
    direccion: string;
    comuna: string;
  }): Promise<{ success: boolean; data: { codigoRetorno: string }; message: string }> {
    try {
      const url = `${this.API_URL}/client/personal-data`;
      
      const requestBody = datosNuevos;

      const response = await fetch(url, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        if (response.status === 401) {
          // Token expirado, intentar refresh
          await this.refreshToken();
          // Reintentar
          return this.updateDatosPersonales(datosNuevos);
        }
        const errorData = await response.json().catch(() => ({
          message: `Error ${response.status}: ${response.statusText}`,
        }));
        throw new Error(errorData.message || 'Error al actualizar datos personales');
      }

      const responseData = await response.json();
      
      // Recargar los datos personales después de actualizar
      await this.loadDatosPersonales();
      
      return responseData;
    } catch (error) {
      console.error('Error al actualizar datos personales:', error);
      throw error;
    }
  }
}
